import React, { Fragment } from 'react';
import { Button, Grid, Typography } from '@material-ui/core';

import { JwlTable, SectionCard, DetailsList, SvgDisplay } from '..';
import {
  GQLPlatformProducerOrder,
  GQLMerchantDeliveryType,
  GQLPlatformProducerOrderProductionPlan
} from '@jewelutions/core-crm-server-api-client-domain';
import { GQLAddressHelper } from '../../operations/helpers/GQLAddressHelper';
import EngravingList from '../EngravingList';
import { CommonLogic } from '@jewelutions/core-common-logic';
import { saveCsvFile } from '../../core/utils';
import { Download } from 'react-feather';

interface IProps {
  order: GQLPlatformProducerOrder;
  productionPlan: GQLPlatformProducerOrderProductionPlan;
}

const PlatformProducerOrderWithProductionPlanDetails: React.FC<IProps> = ({
  order,
  productionPlan
}: IProps) => {
  const getOrderName = (order: GQLPlatformProducerOrder) =>
    order.delivery.deliveryType === GQLMerchantDeliveryType.Warehouse
      ? order.delivery.warehouseDelivery!.companyName
      : `${order.delivery.dropshipDelivery!.personFirstName} ${
          order.delivery.dropshipDelivery!.personLastName
        }`;

  const getOrderLocation = (order: GQLPlatformProducerOrder) =>
    order.delivery.deliveryType === GQLMerchantDeliveryType.Warehouse
      ? GQLAddressHelper.addressToString(order.delivery.warehouseDelivery!.address!)
      : GQLAddressHelper.addressToString(order.delivery.dropshipDelivery!.address!);

  const orderDetailsList = [
    {
      primary: 'ID',
      secondary: order.id
    },
    {
      primary: 'Name',
      secondary: getOrderName(order)
    },
    {
      primary: 'Location',
      secondary: getOrderLocation(order)
    },
    {
      primary: 'State',
      secondary: order.state
    },
    {
      primary: 'Created At',
      secondary: new Date(Date.parse(order.createdAt)).toLocaleString()
    },
    {
      primary: 'Updated At',
      secondary: new Date(Date.parse(order.updatedAt)).toLocaleString()
    }
  ];

  const orderHistoryColumns = ['Date & time', 'State', 'Note'];

  const getOrderHistoryRows = () => {
    return order.history.map((history: any, index: number) => {
      return {
        id: index,
        cells: [
          new Date(Date.parse(history.createdAt)).toLocaleString(),
          history.state,
          ,
          history.note || '-'
        ]
      };
    });
  };

  return (
    <Fragment>
      <Grid item style={{ marginTop: '8px' }}>
        <SectionCard title="Platform Producer Order Details">
          <DetailsList list={orderDetailsList} />
          <JwlTable title="Order History" columns={orderHistoryColumns} rows={getOrderHistoryRows()} />
        </SectionCard>
      </Grid>
      <Grid item style={{ marginTop: '8px' }}>
        <SectionCard title="Order Line Details">
          {productionPlan.items.map((orderLine, index) => {
            return (
              <Grid style={{ marginTop: '8px' }} key={index}>
                <SectionCard title={`Order Line ${index}`} isOutlined>
                  {orderLine.productionPlan.accessoryPlans.map(accessoryPlan => {
                    return (
                      <Grid style={{ marginTop: '8px' }} key={accessoryPlan.id}>
                        <Typography>{`Accessory Plan ${index}`}</Typography>
                        <DetailsList
                          list={[
                            { primary: 'Quantity', secondary: accessoryPlan.quantity },
                            {
                              primary: `Accessory Configuration(Size, Material, Plating)`,
                              secondary: `${accessoryPlan.accessoryReference.accessoryConfiguration.sizeType}, ${accessoryPlan.accessoryReference.accessoryConfiguration.materialType}, ${accessoryPlan.accessoryReference.accessoryConfiguration.platingType}`
                            },
                            {
                              primary: 'Accessory name',
                              secondary: `${
                                orderLine.productionPlan.assets.accessories.find(
                                  p => p.id === accessoryPlan.accessoryReference.accessoryId
                                )?.title.en
                              }`
                            },
                            {
                              primary: 'Inventory Reference',
                              secondary: accessoryPlan.inventoryReference
                            }
                          ]}
                        />
                      </Grid>
                    );
                  })}
                  {orderLine.productionPlan.piecePlans.map(piecePlan => {
                    const currentPiece = orderLine.productionPlan.assets.pieces.find(
                      p => p.id === piecePlan.pieceReference.pieceId
                    )!;
                    const currentPieceModel = currentPiece.models.find(
                      pm => pm.id === piecePlan.pieceReference.pieceModelId
                    )!;

                    return (
                      <Grid style={{ marginTop: '8px' }} key={piecePlan.pieceReference.pieceId}>
                        <Typography>{`Piece Plan ${index}`}</Typography>
                        <DetailsList
                          list={[
                            { primary: 'Quantity', secondary: piecePlan.quantity },
                            {
                              primary: `Piece Configuration(Size, Material, Plating)`,
                              secondary: `${piecePlan.pieceReference.pieceConfiguration.sizeType}, ${piecePlan.pieceReference.pieceConfiguration.materialType}, ${piecePlan.pieceReference.pieceConfiguration.platingType}`
                            },
                            {
                              primary: 'Piece name',
                              secondary: `${
                                orderLine.productionPlan.assets.pieces.find(
                                  p => p.id === piecePlan.pieceReference.pieceId
                                )?.title.en
                              }`
                            },
                            {
                              primary: 'Inventory Reference',
                              secondary: piecePlan.inventoryReference
                            }
                          ]}
                        />
                        <p>Engravings</p>
                        <EngravingList
                          editableAreas={currentPieceModel.editableAreas}
                          engravings={piecePlan.engravings!}
                        />
                        {piecePlan.pieceSides.map(pieceSide => {
                          return (
                            <div key={pieceSide.side}>
                              <p>Piece ${pieceSide.side} side vector preview</p>
                              <SvgDisplay
                                style={{ width: '256px', height: '256px' }}
                                base64Content={pieceSide.vector.contentBase64!}
                              />
                              <p>Piece {pieceSide.side} side vector file</p>
                              <Button
                                variant="contained"
                                color="primary"
                                onClick={() => {
                                  const bytes = CommonLogic.Base64.fromBase64(
                                    pieceSide.vector.contentBase64!
                                  );
                                  saveCsvFile(bytes, `${pieceSide.side}.svg`);
                                }}
                              >
                                <Download />
                              </Button>
                            </div>
                          );
                        })}
                      </Grid>
                    );
                  })}
                </SectionCard>
              </Grid>
            );
          })}
        </SectionCard>
      </Grid>
    </Fragment>
  );
};

export default PlatformProducerOrderWithProductionPlanDetails;
